import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Divider,
  Badge,
  Spinner,
  Center,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import { auditLogsService } from '../services/apiService';
import { useApp } from '../context/AppContext';

// ترتيب ثابت لعرض الحقول في السجل
const FIELD_ORDER = {
  case: ['case_number', 'reference_letter_number', 'requesting_authority', 'letter_date', 'letter_date_hijri', 'case_subject', 'category_id', 'type_id', 'created_by', 'created_at', 'location_id', 'assignedUsers'],
  sample: ['sample_number', 'case_id', 'surface_type', 'description', 'examination_material', 'sample_image', 'sample_image_path', 'image_reference'],
  trace: ['trace_number', 'case_id', 'sample_id', 'status', 'decision', 'name', 'identity_number', 'notes', 'trace_image', 'trace_image_path'],
};

const AuditLogModal = ({ isOpen, onClose, caseId, caseNumber }) => {
  const { users, locations, loadUsers, loadLocations } = useApp();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Color mode values
  const oldValuesBg = useColorModeValue('gray.50', 'gray.700');
  const newValuesBg = useColorModeValue('green.50', 'green.900');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const labelColor = useColorModeValue('gray.800', 'gray.300');
  const valueColor = useColorModeValue('gray.700', 'gray.400');

  const loadLogs = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const data = await auditLogsService.getAllByCase(caseId);
      setLogs(data || []);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (isOpen && caseId) {
      // Load users and locations if not already loaded
      if (users.length === 0) {
        loadUsers(1);
      }
      if (locations.length === 0) {
        loadLocations(1);
      }
      loadLogs();
    }
  }, [isOpen, caseId, loadLogs, users.length, locations.length, loadUsers, loadLocations]);

  const getActionColor = (action) => {
    const colors = {
      create: 'green',
      update: 'blue',
      delete: 'red',
    };
    return colors[action] || 'gray';
  };

  const getEntityTypeLabel = (entityType) => {
    const labels = {
      case: 'قضية',
      sample: 'عينة',
      trace: 'أثر',
    };
    return labels[entityType] || entityType;
  };

  // Field labels in Arabic
  const getFieldLabel = (fieldName, entityType) => {
    const labels = {
      case: {
        case_number: 'رقم القضية',
        reference_letter_number: 'رقم خطاب الإحالة',
        requesting_authority: 'الجهة الطالبة',
        letter_date: 'تاريخ الخطاب',
        letter_date_hijri: 'تاريخ الخطاب (هجري)',
        case_subject: 'موضوع القضية',
        category_id: 'تصنيف القضية',
        type_id: 'نوع القضية',
        created_by: 'منشئ القضية',
        created_at: 'تاريخ إنشاء القضية',
        location_id: 'الموقع',
        assignedUsers: 'المستخدمون المعينون',
      },
      sample: {
        sample_number: 'رقم العينة',
        case_id: 'رقم القضية',
        surface_type: 'نوع السطح',
        description: 'الوصف',
        examination_material: 'مادة الفحص',
        sample_image: 'صورة العينة',
        sample_image_path: 'مسار صورة العينة',
        image_reference: 'مرجع الصورة',
      },
      trace: {
        trace_number: 'رقم الأثر',
        case_id: 'رقم القضية',
        sample_id: 'رقم العينة',
        status: 'الحالة',
        decision: 'القرار',
        name: 'الاسم',
        identity_number: 'رقم الهوية',
        notes: 'ملاحظات',
        trace_image: 'صورة الأثر',
        trace_image_path: 'مسار صورة الأثر',
      },
    };

    return labels[entityType]?.[fieldName] || fieldName;
  };

  const formatFieldValue = (fieldName, value, entityType) => {
    if (value === null || value === undefined || value === '' || (typeof value === 'string' && value.toLowerCase() === 'undefined')) {
      return 'غير محدد';
    }

    // عرض رقم القضية بدلاً من معرف القضية عند عرض سجل نفس القضية
    if (fieldName === 'case_id' && caseNumber && (String(value) === String(caseId))) {
      return caseNumber;
    }

    // مسار صورة العينة / الأثر
    if (fieldName === 'sample_image_path' || fieldName === 'trace_image_path') {
      return value && String(value).trim() !== '' ? 'مسار موجود' : 'غير محدد';
    }

    // Handle assignedUsers array
    if (fieldName === 'assignedUsers' && Array.isArray(value)) {
      if (value.length === 0) {
        return 'لا يوجد مستخدمون معينون';
      }
      return value.map(user => {
        if (typeof user === 'object' && user !== null) {
          // If user object has full_name or fullName
          const userName = user.full_name || user.fullName || user.username || `مستخدم #${user.id}`;
          return userName;
        } else if (typeof user === 'number') {
          // If it's just an ID, try to find user name
          const foundUser = users.find(u => u.id === user);
          return foundUser ? (foundUser.fullName || foundUser.full_name || foundUser.username) : `مستخدم #${user}`;
        }
        return String(user);
      }).join('، ');
    }

    // Handle created_by (user ID)
    if (fieldName === 'created_by') {
      const userId = typeof value === 'number' ? value : parseInt(value);
      const foundUser = users.find(u => u.id === userId);
      if (foundUser) {
        return `${foundUser.fullName || foundUser.full_name || foundUser.username} (${foundUser.username})`;
      }
      return `مستخدم #${value}`;
    }

    // Handle location_id
    if (fieldName === 'location_id') {
      const locationId = typeof value === 'number' ? value : parseInt(value);
      const foundLocation = locations.find(l => l.id === locationId);
      if (foundLocation) {
        const locationCode = foundLocation.code ? ` (${foundLocation.code})` : '';
        return `${foundLocation.name}${locationCode}`;
      }
      return `موقع #${value}`;
    }

    // Handle created_at - format datetime properly
    if (fieldName === 'created_at') {
      if (typeof value === 'string') {
        // Parse and format the datetime
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            // Format as YYYY-MM-DD HH:mm:ss
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
          }
        } catch (e) {
          // If parsing fails, return as is
        }
      }
    }

    // Handle letter_date - format datetime properly
    if (fieldName === 'letter_date') {
      if (typeof value === 'string') {
        // Parse and format the datetime
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            // Format as YYYY-MM-DD HH:mm:ss
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
          }
        } catch (e) {
          // If parsing fails, return as is
        }
      }
    }

    // Handle special field types
    if (fieldName === 'surface_type') {
      const labels = {
        'Porous': 'مسامي',
        'Non-Porous': 'غير مسامي',
        'Semi-Porous': 'شبه مسامي',
      };
      return labels[value] || value;
    }

    if (fieldName === 'status') {
      const labels = {
        'Valid': 'صالح',
        'Invalid': 'غير صالح',
        'Suitable for Examination': 'صالح للفحص',
        'Not Suitable for Examination': 'غير صالح للفحص',
        'Under Examination': 'قيد الفحص',
        'Examination Completed': 'اكتمل الفحص',
      };
      return labels[value] || value;
    }

    if (fieldName === 'decision') {
      const labels = {
        'Match': 'تطابق',
        'Mismatch': 'عدم تطابق',
      };
      return labels[value] || value;
    }

    if (fieldName === 'sample_image' || fieldName === 'trace_image') {
      return value === '[Image Data]' ? 'صورة موجودة' : (value ? 'صورة موجودة' : 'لا توجد صورة');
    }

    if (fieldName === 'category_id' || fieldName === 'type_id') {
      return String(value);
    }

    if (typeof value === 'boolean') {
      return value ? 'نعم' : 'لا';
    }

    return String(value);
  };

  const getOrderedEntries = (data, entityType) => {
    if (!data || typeof data !== 'object') return [];
    const order = FIELD_ORDER[entityType];
    const entries = Object.entries(data);
    if (!order || order.length === 0) return entries;
    return entries.sort(([a], [b]) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  };

  const renderReadableData = (data, entityType) => {
    const entries = getOrderedEntries(data, entityType);
    if (entries.length === 0) {
      return (
        <Text fontSize="sm" color="gray.500" fontStyle="italic">
          لا توجد بيانات
        </Text>
      );
    }

    return (
      <VStack align="stretch" spacing={2}>
        {entries.map(([key, value]) => (
          <Flex key={key} justify="space-between" align="flex-start" py={1} borderBottom="1px solid" borderColor={borderColor}>
            <Text fontSize="sm" fontWeight="semibold" color={labelColor} minW="150px">
              {getFieldLabel(key, entityType)}:
            </Text>
            <Text fontSize="sm" color={valueColor} textAlign="left" flex={1} dir="ltr">
              {formatFieldValue(key, value, entityType)}
            </Text>
          </Flex>
        ))}
      </VStack>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>سجل العمليات</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <Center py={8}>
              <Spinner size="xl" />
            </Center>
          ) : logs.length === 0 ? (
            <Center py={8}>
              <Text color="gray.500">لا توجد عمليات مسجلة</Text>
            </Center>
          ) : (
            <VStack align="stretch" spacing={4} maxH="70vh" overflowY="auto">
              {logs.map((log, index) => (
                <Box key={log.id} borderWidth="1px" borderRadius="md" p={4}>
                  <HStack justify="space-between" mb={2}>
                    <HStack spacing={2}>
                      <Badge colorScheme={getActionColor(log.action)}>
                        {log.actionLabel}
                      </Badge>
                      <Badge variant="outline">
                        {getEntityTypeLabel(log.entityType)}
                      </Badge>
                    </HStack>
                    <VStack align="flex-end" spacing={0}>
                      {log.createdAtHijriFormatted && (
                        <Text fontSize="sm" color="gray.500" fontWeight="semibold">
                          {log.createdAtHijriFormatted}
                        </Text>
                      )}
                      <Text fontSize="xs" color="gray.400">
                        {log.createdAtFormatted || log.createdAt}
                      </Text>
                    </VStack>
                  </HStack>
                  
                  {log.user && (
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      المستخدم: <strong>{log.user.fullName}</strong> ({log.user.username})
                    </Text>
                  )}

                  {log.description && (
                    <Text fontSize="sm" mb={2}>
                      {log.description}
                    </Text>
                  )}

                  {(log.oldValues || log.newValues) && (
                    <Accordion allowToggle>
                      {(log.oldValues && Object.keys(log.oldValues).length > 0) && (
                        <AccordionItem>
                          <AccordionButton>
                            <Box flex="1" textAlign="right">
                              القيم السابقة
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel pb={4}>
                            <Box
                              p={3}
                              borderRadius="md"
                              bg={oldValuesBg}
                              maxH="300px"
                              overflowY="auto"
                            >
                              {renderReadableData(log.oldValues, log.entityType)}
                            </Box>
                          </AccordionPanel>
                        </AccordionItem>
                      )}
                      {(log.newValues && Object.keys(log.newValues).length > 0) && (
                        <AccordionItem>
                          <AccordionButton>
                            <Box flex="1" textAlign="right">
                              القيم الجديدة
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel pb={4}>
                            <Box
                              p={3}
                              borderRadius="md"
                              bg={newValuesBg}
                              maxH="300px"
                              overflowY="auto"
                            >
                              {renderReadableData(log.newValues, log.entityType)}
                            </Box>
                          </AccordionPanel>
                        </AccordionItem>
                      )}
                    </Accordion>
                  )}

                  {index < logs.length - 1 && <Divider mt={4} />}
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>إغلاق</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AuditLogModal;

